<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { navItems, type NavItem } from '@/app/config/navigation'
import { useAuthStore } from '@/stores/auth.store'
import { useUiStore } from '@/stores/ui.store'

/** `collapsed` hanya berlaku di sidebar desktop; versi Sheet selalu penuh. */
withDefaults(defineProps<{ onNavigate?: () => void; collapsed?: boolean }>(), {
  collapsed: false,
})

const auth = useAuthStore()
const ui = useUiStore()
const route = useRoute()

/** Izin item → array (permission bisa string tunggal atau array). */
function permsOf(item: NavItem): string[] {
  if (!item.permission) return []
  return Array.isArray(item.permission) ? item.permission : [item.permission]
}

/** Item tampil bila tak ada izin, ATAU user memenuhi salah satunya. Jika superadminOnly, hanya tampil untuk superadmin. */
function isVisible(item: NavItem): boolean {
  if (item.superadminOnly && !auth.user?.isSuperadmin) return false
  return auth.canAny(permsOf(item))
}

/** Grup: sembunyikan bila tak ada satu pun anak yang tampil. */
const visibleItems = computed(() =>
  navItems
    .filter(isVisible)
    .map((item) => (item.children ? { ...item, children: item.children.filter(isVisible) } : item))
    .filter((item) => !item.children || item.children.length > 0),
)

/** Grup terbuka otomatis bila salah satu anak adalah route aktif. */
function isGroupActive(item: NavItem): boolean {
  return Boolean(item.children?.some((c) => c.to && route.path.startsWith(c.to)))
}

/** State buka-tutup grup menu. Menggunakan state reaktif Vue murni agar tidak bentrok dengan DOM HTML native. */
const openGroups = ref<Record<string, boolean>>({})

function isGroupOpen(item: NavItem): boolean {
  if (openGroups.value[item.label] !== undefined) {
    return openGroups.value[item.label]
  }
  return isGroupActive(item)
}

function toggleGroup(label: string) {
  const item = visibleItems.value.find((i) => i.label === label)
  const currentlyOpen = isGroupOpen(item ?? { label })
  openGroups.value[label] = !currentlyOpen
}

// Buka otomatis grup yang anaknya sedang aktif setiap route berganti
watch(
  () => route.path,
  () => {
    for (const item of visibleItems.value) {
      if (item.children && isGroupActive(item)) {
        openGroups.value[item.label] = true
      }
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="flex h-full flex-col">
    <div class="flex h-16 items-center gap-2" :class="collapsed ? 'justify-center px-0' : 'px-5'">
      <div
        class="relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/10 p-1.5 shadow-xs border border-primary/20"
      >
        <img src="/logo.png" alt="POS Logo" class="h-full w-full object-contain" />
      </div>
      <div v-if="!collapsed" class="leading-tight">
        <p class="font-semibold">POS Dashboard</p>
        <p class="text-muted-foreground text-xs">{{ auth.user?.tenant?.name ?? 'POS' }}</p>
      </div>
    </div>

    <nav class="flex-1 space-y-1 py-2" :class="collapsed ? 'px-2' : 'px-3'">
      <template v-for="item in visibleItems" :key="item.label">
        <!--
          Saat menyempit, grup tidak bisa membuka daftar anaknya — tidak ada
          ruang untuk labelnya. Ikonnya jadi tombol yang MELEBARKAN sidebar,
          bukan tombol mati: satu klik mengembalikan konteks yang dibutuhkan
          untuk memilih anaknya.
        -->
        <button
          v-if="item.children && collapsed"
          type="button"
          :title="item.label"
          class="text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex w-full items-center justify-center rounded-lg px-3 py-2 transition-colors"
          :class="isGroupActive(item) && 'bg-sidebar-accent text-sidebar-accent-foreground'"
          @click="ui.toggleSidebar(false)"
        >
          <component :is="item.icon" v-if="item.icon" class="size-4.5" />
          <span class="sr-only">{{ item.label }}</span>
        </button>

        <!-- Grup collapsible -->
        <div v-else-if="item.children" class="group">
          <button
            type="button"
            class="text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            :class="isGroupActive(item) && 'text-sidebar-accent-foreground font-semibold'"
            @click="toggleGroup(item.label)"
          >
            <component :is="item.icon" v-if="item.icon" class="size-4.5" />
            <span class="flex-1 text-left">{{ item.label }}</span>
            <ChevronDown
              class="size-4 transition-transform duration-200"
              :class="isGroupOpen(item) ? 'rotate-180' : ''"
            />
          </button>
          <div v-show="isGroupOpen(item)" class="mt-1 space-y-1 pl-4">
            <RouterLink
              v-for="child in item.children"
              :key="child.to"
              :to="child.to!"
              active-class="bg-sidebar-accent text-sidebar-accent-foreground"
              class="text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
              @click="onNavigate?.()"
            >
              <component :is="child.icon" v-if="child.icon" class="size-4.5" />
              {{ child.label }}
            </RouterLink>
          </div>
        </div>

        <!-- Item tunggal -->
        <RouterLink
          v-else
          :to="item.to!"
          :title="collapsed ? item.label : undefined"
          active-class="bg-sidebar-accent text-sidebar-accent-foreground"
          exact-active-class="bg-sidebar-primary text-sidebar-primary-foreground"
          class="text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
          :class="collapsed && 'justify-center'"
          @click="onNavigate?.()"
        >
          <component :is="item.icon" v-if="item.icon" class="size-4.5" />
          <template v-if="!collapsed">{{ item.label }}</template>
          <span v-else class="sr-only">{{ item.label }}</span>
        </RouterLink>
      </template>
    </nav>

    <div v-if="!collapsed" class="text-muted-foreground px-5 py-4 text-xs">v0.1.0 · © 2026</div>
  </div>
</template>
